/**
 * The API entry point. main.tsx calls `configureApi` with the boot probe's config before the
 * first render; `api` forwards each call to the transport that this makes.
 *
 * The active organization is not in this config. It is per user: LoginPage sets it through
 * authSession.setActiveOrg, and the cloud transport reads it on each call.
 *
 * Tests install a fake with `installTestTransport` (src/test/render.tsx does this).
 */

import type { ClientConfig, VmsApi } from '@/types/api';

import { createCloudTransport } from './transports/cloud';

export type { VmsApi } from '@/types/api';

const NOT_CONFIGURED = 'api: configureApi has not run';

/** The live transport, the config it was made from, and the test seam over both. */
class ApiSlot {
    private config: ClientConfig | null = null;
    private transport: VmsApi | null = null;
    // `null` is a valid live transport (nothing configured), so a flag tells "installed" apart.
    private isInstalled = false;
    private beforeInstall: VmsApi | null = null;

    getLive(): VmsApi {
        if (this.transport === null) {
            throw new Error(NOT_CONFIGURED);
        }
        return this.transport;
    }

    getConfig(): ClientConfig {
        if (this.config === null) {
            throw new Error(NOT_CONFIGURED);
        }
        return this.config;
    }

    configure(config: ClientConfig): void {
        this.config = config;
        this.transport = createCloudTransport();
    }

    install(transport: VmsApi): void {
        if (!this.isInstalled) {
            this.beforeInstall = this.transport;
            this.isInstalled = true;
        }
        this.transport = transport;
    }

    reset(): void {
        if (!this.isInstalled) {
            return;
        }
        this.transport = this.beforeInstall;
        this.beforeInstall = null;
        this.isInstalled = false;
    }
}

const slot = new ApiSlot();

// A Proxy rather than a re-exported binding so configureApi can swap the
// transport under every module that already imported `api`.
export const api: VmsApi = new Proxy({} as VmsApi, {
    get(_t, prop): unknown { return slot.getLive()[prop as keyof VmsApi]; },
    has(_t, prop): boolean { return prop in slot.getLive(); },
    ownKeys(): (string | symbol)[] { return Reflect.ownKeys(slot.getLive()); },
    getOwnPropertyDescriptor(_t, prop): PropertyDescriptor | undefined {
        return Reflect.getOwnPropertyDescriptor(slot.getLive(), prop);
    },
});

/** The boot probe's config: the Google client id. */
export function getApiConfig(): ClientConfig {
    return slot.getConfig();
}

export function configureApi(config: ClientConfig): void {
    slot.configure(config);
}

/** Test seam: swaps in a fake transport under every module holding `api`. */
export function installTestTransport(transport: VmsApi): void {
    slot.install(transport);
}

/** Restores the transport that was live before the first install. */
export function resetTransport(): void {
    slot.reset();
}
